import { MediaItem, MediaItemSegment } from "./data/types.js";
import { SearchResult } from "./server/media.js";
import { error } from "./utils/utils.js";

export interface ChatMessage {
    role: "system" | "assistant" | "user";
    content: string;
}

export type ChatContextItem =
    | {
          type: "item";
          item: MediaItem;
      }
    | {
          type: "segment";
          item: MediaItem;
          segment: MediaItemSegment;
      };

export interface ChatSession {
    id: string | null;
    messages: ChatMessage[];
    context: ChatContextItem[];
    lastModified: number;
}

export interface CompletionRequest {
    sessionId: string;
    message: string;
}

export interface User {
    id: string;
    name: string;
    email: string;
    password_hash: string;
    role: "user" | "admin";
    email_confirmed: boolean;
    created_at: Date;
}

export type PublicUser = Omit<User, "password_hash">;

export interface JsonValue {
    [key: string]: any;
}

interface SourceMetadata {
    sourcePath: string;
    shortName?: string;
    displayName: string;
    paths: Array<{ path: string; count: number }>;
}

interface SourcesResponse {
    sources: SourceMetadata[];
    totalDocuments: number;
}

function apiBaseUrl() {
    return "/api/";
}

export async function apiGet<T>(endpoint: string) {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        if (!result.ok) throw new Error();
        return (await result.json()) as T;
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiGetText(endpoint: string): Promise<string | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        if (!result.ok) throw new Error();
        return await result.text();
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiGetBlob(endpoint: string): Promise<Blob | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        if (!result.ok) throw new Error();
        return await result.blob();
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiGetArrayBuffer(endpoint: string): Promise<ArrayBuffer | Error> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint);
        if (!result.ok) throw new Error();
        return await result.arrayBuffer();
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiPost<T>(endpoint: string, params: URLSearchParams | FormData | JsonValue | Blob | ArrayBuffer, contentType?: string) {
    let headers: HeadersInit = {};
    let body: string | FormData | Blob | ArrayBuffer;

    if (params instanceof URLSearchParams) {
        headers = { "Content-Type": "application/x-www-form-urlencoded" };
        body = params.toString();
    } else if (params instanceof FormData) {
        body = params;
    } else if (params instanceof Blob || params instanceof ArrayBuffer) {
        // Handle binary data
        if (contentType) {
            headers = { "Content-Type": contentType };
        } else if (params instanceof Blob) {
            headers = { "Content-Type": params.type || "application/octet-stream" };
        } else {
            headers = { "Content-Type": "application/octet-stream" };
        }
        body = params;
    } else {
        // Handle JSON data
        headers = { "Content-Type": "application/json" };
        body = JSON.stringify(params);
    }

    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "POST",
            headers: headers,
            body: body,
        });
        if (!result.ok) throw new Error();
        return (await result.json()) as T;
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiDelete(endpoint: string): Promise<void> {
    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "DELETE",
        });
        if (!result.ok) throw new Error();
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export async function apiPut<T>(endpoint: string, params: JsonValue) {
    try {
        const result = await fetch(apiBaseUrl() + endpoint, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params),
        });
        if (!result.ok) throw new Error();
        return (await result.json()) as T;
    } catch (e) {
        throw error(`Request /api/${endpoint} failed`, e);
    }
}

export interface TokenUsage {
    inputTokens: number;
    outputTokens: number;
}

export interface CreateUserRequest {
    name: string;
    email: string;
    password: string;
    role: "user" | "admin";
}

interface GetUsersResponse {
    users: User[];
    total: number;
}

interface GetSearchEventsResponse {
    events: SearchEvent[];
    total: number;
}

interface GetCompletionEventsResponse {
    events: CompletionEvent[];
    total: number;
}

export interface SearchEvent {
    id: string;
    user_id: string;
    timestamp: Date;
    query: string | null;
    paths: string[] | null;
    date_from: Date | null;
    date_to: Date | null;
}

export interface CompletionEvent {
    id: string;
    user_id: string;
    timestamp: Date;
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    session_id: string | null;
}

export interface SearchEventsResponse {
    events: SearchEvent[];
    total: number;
}

export interface CompressedChatContextItem {
    type: "item" | "segment";
    itemId: string;
    segmentIndex?: number;
}

export interface StoredChatSession {
    id: string;
    user_id: string;
    messages: ChatMessage[];
    context: CompressedChatContextItem[];
    created_at: Date;
    last_modified: Date;
    deleted_at: Date | null;
}

export interface CompleteMessagesRequest {
    sessionId: string;
    messages: ChatMessage[];
    context: CompressedChatContextItem[];
}

export interface SearchTiming {
    timestamp: number;
    total: number;
    session: number;
    record: number;
    search: number;
    serialize: number;
    query?: string;
}

export interface SearchPerformanceStats {
    timings: SearchTiming[];
    summary: {
        count: number;
        averages: {
            total: number;
            session: number;
            record: number;
            search: number;
            serialize: number;
        };
    };
}

export class Api {
    static async hello(): Promise<{ message: string }> {
        return apiGet<{ message: string }>("hello");
    }

    static async search(options: {
        query?: string;
        paths?: string[];
        dateFrom?: Date;
        dateTo?: Date;
        page?: number;
        pageSize?: number;
        sort?: "asc" | "desc";
    }): Promise<SearchResult> {
        const params = new URLSearchParams();
        if (options.query) params.append("q", options.query);
        if (options.paths?.length) params.append("paths", options.paths.join(","));
        if (options.dateFrom) params.append("dateFrom", options.dateFrom.toISOString());
        if (options.dateTo) params.append("dateTo", options.dateTo.toISOString());
        if (typeof options.page == "number") params.append("page", options.page.toString());
        if (typeof options.pageSize == "number") params.append("pageSize", options.pageSize.toString());
        if (options.sort) params.append("sort", options.sort);

        return apiGet<SearchResult>("search?" + params.toString());
    }

    static async session(): Promise<ChatSession> {
        return apiGet<ChatSession>("session");
    }

    static async complete(sessionId: string, message: string, chunkCb: (chunk: string, done: boolean) => void): Promise<TokenUsage | undefined> {
        try {
            const response = await fetch("/api/complete", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ sessionId, message }),
            });

            if (!response.ok || !response.body) return undefined;

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let tokenData: TokenUsage | undefined;

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    chunkCb("", true);
                    break;
                }

                const chunk = decoder.decode(value);
                if (chunk.includes("\n__TOKENS__")) {
                    const [content, tokenInfo] = chunk.split("\n__TOKENS__");
                    if (content) chunkCb(content, false);
                    tokenData = JSON.parse(tokenInfo);
                } else {
                    chunkCb(chunk, false);
                }
            }

            return tokenData;
        } catch (e) {
            console.error("Complete error:", e);
            return undefined;
        }
    }

    static async getSources(): Promise<SourcesResponse> {
        return apiGet<SourcesResponse>("sources");
    }

    static async *streamDocuments(
        options: {
            batchSize?: number;
            onProgress?: (count: number) => void;
        } = {}
    ): AsyncGenerator<MediaItem, void, unknown> {
        const batchSize = options.batchSize || 1000;
        let count = 0;

        const response = await fetch(`${apiBaseUrl()}items?batchSize=${batchSize}`, { headers: { Accept: "application/x-ndjson" } });

        if (!response.ok || !response.body) {
            throw new Error("Failed to fetch documents stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let newlineIndex;
                while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                    const line = buffer.slice(0, newlineIndex);
                    buffer = buffer.slice(newlineIndex + 1);

                    if (line.trim()) {
                        const doc = JSON.parse(line);
                        count++;
                        if (options.onProgress) {
                            options.onProgress(count);
                        }
                        yield doc;
                    }
                }
            }

            // Handle any remaining data
            if (buffer.trim()) {
                const doc = JSON.parse(buffer);
                count++;
                if (options.onProgress) {
                    options.onProgress(count);
                }
                yield doc;
            }
        } finally {
            reader.cancel();
        }
    }

    static async getItems(
        options: {
            onProgress?: (count: number) => void;
        } = {}
    ): Promise<MediaItem[]> {
        const documents: MediaItem[] = [];
        for await (const doc of this.streamDocuments(options)) {
            documents.push(doc);
        }
        return documents;
    }

    static async completeMessages(
        request: CompleteMessagesRequest,
        chunkCb: (chunk: string, done: boolean) => void
    ): Promise<TokenUsage | undefined> {
        try {
            const response = await fetch("/api/complete-messages", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
            });

            if (!response.ok || !response.body) {
                throw new Error("Failed to get response from LLM");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let tokenData: TokenUsage | undefined;

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    chunkCb("", true);
                    break;
                }

                const chunk = decoder.decode(value);
                if (chunk.includes("\n__TOKENS__")) {
                    const [content, tokenInfo] = chunk.split("\n__TOKENS__");
                    if (content) chunkCb(content, false);
                    tokenData = JSON.parse(tokenInfo);
                } else {
                    chunkCb(chunk, false);
                }
            }

            return tokenData;
        } catch (e) {
            console.error("Complete messages error:", e);
            throw e;
        }
    }

    static async login(email: string, password: string): Promise<User> {
        return apiPost<User>("auth/login", { email, password });
    }

    static async register(name: string, email: string, password: string, invitationCode?: string): Promise<User> {
        const endpoint = invitationCode ? `auth/register?invitation=${invitationCode}` : "auth/register";
        return apiPost<User>(endpoint, { name, email, password });
    }

    static async me(): Promise<User> {
        return apiGet<User>("auth/me");
    }

    static async logout(): Promise<{ success: boolean }> {
        return apiPost<{ success: boolean }>("auth/logout", {});
    }

    static async getUsers(page: number = 0, pageSize: number = 20): Promise<GetUsersResponse> {
        return apiGet<GetUsersResponse>(`admin/users?page=${page}&pageSize=${pageSize}`);
    }

    static async getUser(id: string): Promise<User> {
        return apiGet<User>(`admin/users/${id}`);
    }

    static async createUser(request: CreateUserRequest): Promise<User> {
        return apiPost<User>("admin/users", request);
    }

    static async deleteUser(id: string): Promise<void> {
        return apiDelete(`admin/users/${id}`);
    }

    static async updateUserRole(id: string, role: "user" | "admin"): Promise<User> {
        return apiPut<User>(`admin/users/${id}/role`, { role });
    }

    static async getSearchEvents(
        options: {
            page?: number;
            pageSize?: number;
            userId?: string;
        } = {}
    ): Promise<SearchEventsResponse> {
        const params = new URLSearchParams();
        if (typeof options.page === "number") params.append("page", options.page.toString());
        if (typeof options.pageSize === "number") params.append("pageSize", options.pageSize.toString());
        if (options.userId) params.append("userId", options.userId);

        return apiGet<SearchEventsResponse>(`admin/search-events?${params.toString()}`);
    }

    static async getCompletionEvents(
        options: {
            page?: number;
            pageSize?: number;
            userId?: string;
        } = {}
    ): Promise<GetCompletionEventsResponse> {
        const params = new URLSearchParams();
        if (typeof options.page === "number") params.append("page", options.page.toString());
        if (typeof options.pageSize === "number") params.append("pageSize", options.pageSize.toString());
        if (options.userId) params.append("userId", options.userId);

        return apiGet<GetCompletionEventsResponse>(`admin/completion-events?${params.toString()}`);
    }

    static async getCompletionStats(options: {
        userId?: string;
        dateFrom: Date;
        dateTo: Date;
    }): Promise<{ date: string; input_tokens: number; output_tokens: number }[]> {
        const params = new URLSearchParams();
        if (options.userId) params.append("userId", options.userId);
        params.append("dateFrom", options.dateFrom.toISOString());
        params.append("dateTo", options.dateTo.toISOString());

        return apiGet<{ date: string; input_tokens: number; output_tokens: number }[]>(`admin/completion-stats?${params.toString()}`);
    }

    static async getChats(
        options: {
            page?: number;
            pageSize?: number;
        } = {}
    ): Promise<{ sessions: StoredChatSession[]; total: number }> {
        const params = new URLSearchParams();
        if (typeof options.page === "number") params.append("page", options.page.toString());
        if (typeof options.pageSize === "number") params.append("pageSize", options.pageSize.toString());

        return apiGet<{ sessions: StoredChatSession[]; total: number }>(`chats?${params.toString()}`);
    }

    static async getAdminChats(
        options: {
            page?: number;
            pageSize?: number;
            userId?: string;
        } = {}
    ): Promise<{ sessions: StoredChatSession[]; total: number }> {
        const params = new URLSearchParams();
        if (typeof options.page === "number") params.append("page", options.page.toString());
        if (typeof options.pageSize === "number") params.append("pageSize", options.pageSize.toString());
        if (options.userId) params.append("userId", options.userId);

        return apiGet<{ sessions: StoredChatSession[]; total: number }>(`admin/chats?${params.toString()}`);
    }

    static async getItemsByIds(ids: string[]): Promise<MediaItem[]> {
        if (ids.length === 0) return [];

        const params = new URLSearchParams();
        params.append("ids", ids.join(","));

        const response = await fetch(`${apiBaseUrl()}items/by-ids?${params.toString()}`, {
            headers: { Accept: "application/x-ndjson" },
        });

        if (!response.ok || !response.body) {
            throw new Error("Failed to fetch items");
        }

        const items: MediaItem[] = [];
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let newlineIndex;
                while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
                    const line = buffer.slice(0, newlineIndex);
                    buffer = buffer.slice(newlineIndex + 1);

                    if (line.trim()) {
                        items.push(JSON.parse(line));
                    }
                }
            }

            // Handle any remaining data
            if (buffer.trim()) {
                items.push(JSON.parse(buffer));
            }

            return items;
        } finally {
            reader.cancel();
        }
    }

    static async deleteChat(id: string): Promise<void> {
        return apiDelete(`chats/${id}`);
    }

    static async getAllChatSessions(
        options: {
            page?: number;
            pageSize?: number;
        } = {}
    ): Promise<{ sessions: StoredChatSession[]; total: number }> {
        const params = new URLSearchParams();
        if (typeof options.page === "number") params.append("page", options.page.toString());
        if (typeof options.pageSize === "number") params.append("pageSize", options.pageSize.toString());

        return apiGet<{ sessions: StoredChatSession[]; total: number }>(`admin/chats?${params.toString()}`);
    }

    static async createInvitation(): Promise<{ code: string }> {
        return apiPost<{ code: string }>("admin/invitations", {});
    }

    static async getValidInvitations(): Promise<{ invitations: { id: string; code: string; created_by: string; created_at: Date }[] }> {
        return apiGet<{ invitations: { id: string; code: string; created_by: string; created_at: Date }[] }>("admin/invitations");
    }

    static async deleteInvitation(id: string): Promise<void> {
        return apiDelete(`admin/invitations/${id}`);
    }

    static async executeAdminSQL(query: string): Promise<any[]> {
        const response = await fetch("/api/admin/sql", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ query }),
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error);
        }

        return response.json();
    }

    static async getSearchPerformance(limit?: number): Promise<SearchPerformanceStats> {
        const params = new URLSearchParams();
        if (limit) params.append("limit", limit.toString());
        return apiGet<SearchPerformanceStats>(`search/performance?${params.toString()}`);
    }
}
